import "zod/compile";
import { z } from "zod";

export const workbookDiagnosticCodeSchema = z.enum([
  "formula-syntax",
  "unknown-function",
  "qualified-reference",
  "unknown-column",
  "unknown-table",
  "table-reference",
  "lookup-mode",
  "approximate-match",
  "lookup-column-index",
  "argument-type",
  "text-format",
  "aggregate-criteria",
  "column-cycle",
  "duplicate-column",
  "column-case-mismatch",
  "reserved-name",
  "missing-input",
  "sheet-cycle",
  "missing-lookup-source",
  "unknown-lookup-column",
  "lookup-output-collision",
  "lookup-type-mismatch",
  "unknown-field",
  "empty-value-list",
  "validation-range",
  "validation-pattern",
  "validation-not-boolean",
  "validation-sheet",
  // Pivot Worksheets.
  "empty-pivot",
  "pivot-date-group",
  "pivot-value-type",
  "pivot-sort-value",
  "pivot-column-keys",
  // Freeform sheets.
  "cell-cycle",
  "freeform-table-header",
  "freeform-table-range",
  "freeform-missing-column",
]);
export type WorkbookDiagnosticCode = z.infer<
  typeof workbookDiagnosticCodeSchema
>;

const spanSchema = z.object({
  start: z.number().int().nonnegative(),
  end: z.number().int().nonnegative(),
});
const index = z.number().int().nonnegative();

/** Where a Diagnostic points: a whole Item, or one part of a Worksheet. */
export const workbookDiagnosticLocationSchema = z.discriminatedUnion("part", [
  z.object({ part: z.literal("item"), itemId: z.string() }),
  z.object({
    part: z.literal("column"),
    itemId: z.string(),
    index,
    /** Character span within the Formula text as stored. */
    span: spanSchema.optional(),
  }),
  z.object({
    part: z.literal("lookup"),
    itemId: z.string(),
    index,
    field: z.enum([
      "sourceId",
      "inputColumn",
      "matchColumn",
      "valueColumn",
      "outputColumn",
    ]),
  }),
  z.object({ part: z.literal("filter"), itemId: z.string(), index }),
  z.object({ part: z.literal("sort"), itemId: z.string(), index }),
  z.object({
    part: z.literal("validation"),
    itemId: z.string(),
    index,
    /** For a Formula Validation, the character span within its Formula text as stored. */
    span: spanSchema.optional(),
  }),
  /** One cell of a freeform sheet, by A1 address. */
  z.object({ part: z.literal("cell"), itemId: z.string(), cell: z.string() }),
  /** One field of a pivot Worksheet's Rows, Columns, Values or Filters. */
  z.object({
    part: z.literal("pivot"),
    itemId: z.string(),
    area: z.enum(["rows", "columns", "values", "filters"]),
    index,
  }),
]);
export type WorkbookDiagnosticLocation = z.infer<
  typeof workbookDiagnosticLocationSchema
>;

/** A static, located problem found without data. Diagnostics block a Run, never a save. */
export const workbookDiagnosticSchema = z.object({
  code: workbookDiagnosticCodeSchema,
  /** English, for the agent and logs; the UI translates by `code`. */
  message: z.string(),
  location: workbookDiagnosticLocationSchema,
});
export type WorkbookDiagnostic = z.infer<typeof workbookDiagnosticSchema>;
